/**
 * @file plotConstants.js
 * @description System-wide constants for plot hardware operational statuses,
 * site size classifications, badge styling rules, and cardinal direction 
 * vectors for wind parsing.
 *
 * @module constants/plotConstants
 * @exports STATUS_BADGES
 * @exports SITE_SIZES
 * @exports CARDINAL_DIRECTIONS
 */
export const STATUS_BADGES = {
  // DB Hardware Statuses (Normalized to lowercase keys)
  pending: { label: 'Pending', bg: '#fef3c7', text: '#b45309' },
  shipped: { label: 'Shipped', bg: '#e0f2fe', text: '#0369a1' },
  delivered: { label: 'Delivered', bg: '#e0e7ff', text: '#4338ca' },
  active: { label: 'Active', bg: '#dcfce7', text: '#15803d' },
  operational: { label: 'Operational', bg: '#dcfce7', text: '#15803d' },
  warning: { label: 'Warning', bg: '#ffedd5', text: '#c2410c' },
  critical: { label: 'Critical', bg: '#fee2e2', text: '#b91c1c' },
  offline: { label: 'Offline', bg: '#f1f5f9', text: '#64748b' },
  unassigned: { label: 'Unassigned', bg: '#f3f4f6', text: '#4b5563' },

  // Legacy / Order status fallbacks
  ordered: { label: 'Ordered', bg: '#fef3c7', text: '#d97706' },
  provisioned: { label: 'Provisioned', bg: '#e0e7ff', text: '#4338ca' }
};

export const SITE_SIZES = [
  { id: 'small', minSize: 0, fallbackLabel: '< 1 Hectare' },
  { id: 'medium', minSize: 1, fallbackLabel: '1 - 5 Hectares' },
  { id: 'large', minSize: 5, fallbackLabel: '5 - 20 Hectares' },
  { id: 'enterprise', minSize: 21, fallbackLabel: '20+ Hectares' }
];

export const CARDINAL_DIRECTIONS = [
  'N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE',
  'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'
];




/**
 * Safe status badge resolver that handles nulls, case differences, and unmapped states.
 * @param {string} status
 * @returns {{ label: string, bg: string, text: string }}
 */
export function getStatusBadge(status) {
  if (!status) {
    return { label: 'Unknown', bg: '#f1f5f9', text: '#64748b' };
  }

  const key = String(status).trim().toLowerCase();
  return STATUS_BADGES[key] || {
    label: String(status).toUpperCase(),
    bg: '#f1f5f9',
    text: '#475569'
  };
}


/**
 * Returns the human-readable fallback label for a given site area in hectares.
 * @param {number} area - Area in hectares.
 * @returns {string} Size range label (e.g., "1 - 5 Hectares").
 */
export function getSiteSizeLabel(area = 0) {
  const numericArea = Number(area) || 0;
  const sortedSizes = [...SITE_SIZES].sort((a, b) => b.minSize - a.minSize);
  const match = sortedSizes.find((s) => numericArea >= s.minSize) || SITE_SIZES[0];
  return match.fallbackLabel;
}

